/**
 * PluginRenderer — Renders a plugin arche in a sandboxed iframe.
 *
 * Responsibilities:
 * - Fetches plugin config from the arche record
 * - Creates sandboxed iframe with strict CSP
 * - Establishes postMessage bridge via PluginBridgeHost
 * - Handles resize, navigation, and toast events
 * - Shows loading/error states
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { API_BASE, API_ORIGIN, ApiError } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { sessionCredentials } from '@/lib/embed-document';
import { testTenantIdForArche } from '@/lib/test-tenant';
import { toast } from '@/components/ui/use-toast';
import { addBreadcrumb } from '@/lib/telemetry';
import { isApiErrorCode } from '@/lib/api-error-code';
import { useRegisterArcheAI, useArcheHandoffIntake } from '@/components/fias-ai';
import { ARCHE_HANDOFF_INTAKE, type ArcheHandoffPayload } from '@/components/fias-ai/handoff';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';
import type {
  ArcheAIAction,
  PluginDeclaredAiAction,
  PluginRuntimeAiDeclaration,
} from '@/components/fias-ai/types';
import { PluginBridgeHost } from './PluginBridge';
import { usePluginTextModelCatalog } from './plugin-text-models';
import { PLUGIN_IFRAME_ALLOW, PLUGIN_IFRAME_SANDBOX } from './pluginSandbox';
import { PurchaseConfirmationSheet } from './PurchaseConfirmationSheet';
import { PLUGIN_STORE_ENTITY_ID } from '@/lib/integration-entity-permissions';
import { VaultAccessGrantSheet, type VaultPickResolution } from './VaultAccessGrantSheet';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { SaveFileDialog } from '@/components/files/file-dialog/SaveFileDialog';
import { fileAcceptFromTypes } from '@/components/files/file-dialog/accept';
import { ArcheLoadingScreen } from './ArcheLoadingScreen';
import { FiasAIDiscoveryChip } from './FiasAIDiscoveryChip';
import { PluginWorkspaceIndicator } from './PluginWorkspaceIndicator';
import { DARK_THEME, LIGHT_THEME, getThemeById, type PluginPermission } from '@fias/arche-sdk';
import { normalizeCdnBaseUrl, allVendoredLibraryAssets } from '@fias/platform-resources';

/** A plugin's request to save into the user's Documents, as the bridge
 *  hands it to the host. `fileName` is plugin-supplied — untrusted. */
type VaultSaveRequestFromBridge = Parameters<
  NonNullable<ConstructorParameters<typeof PluginBridgeHost>[0]['onVaultSaveRequest']>
>[0];
type VaultSaveResolution =
  | { saved: { documentId: string; name: string; access: 'none' | 'read' | 'write' } }
  | { canceled: true };

/**
 * Resolve an `open_arche` bridge request host-side: navigate the top window to
 * another arche's page (`/a/<archeId>`), optionally at a sub-path
 * (`/a/<archeId>/<path>`) so the opener can land the user on a specific page of
 * the target's own router instead of its home screen. `newTab` is best-effort —
 * the parent's `window.open` can be popup-blocked because user activation does
 * not cross the iframe→host postMessage boundary, so we fall back to same-tab
 * navigation when the popup is blocked (returns null) and the click never
 * silently no-ops.
 *
 * `targetArcheId` and `opts.path` are already validated by the bridge before
 * this runs. `windowOpen` is injectable for testing. Exported for unit tests.
 */
export function openArcheFromHost(
  navigate: (path: string, options?: { state?: unknown }) => void,
  targetArcheId: string,
  opts: {
    newTab?: boolean;
    path?: string;
    payload?: ArcheHandoffPayload;
    originArcheId?: string;
    /** GlobalFiasAIContext's `openHandoff` — the ONE fiasAiPrefill producer. */
    openHandoff?: (
      payload: ArcheHandoffPayload,
      targetArcheId: string,
      context: { originArcheId?: string },
    ) => boolean;
  },
  windowOpen: (url: string, target: string, features: string) => Window | null = (url, t, f) =>
    window.open(url, t, f),
): void {
  // The bridge has already validated both halves; this only joins them. An
  // options object rather than more positional args so the `windowOpen` seam
  // (which every test overrides) stays last.
  const url = opts.path ? `/a/${targetArcheId}/${opts.path}` : `/a/${targetArcheId}`;

  // A handoff does NOT navigate from here. `fiasAiPrefill` has exactly one
  // producer (GlobalFiasAIContext.openHandoff, pinned by
  // tests/architecture/arche-handoff-registry.test.ts) so every payload goes
  // through the same validation and in-place/navigate routing. The caller
  // hands us `openHandoff` and we delegate; a plugin's open_arche-with-payload
  // is the same act as the assistant's open_arche-with-prefill.
  if (opts.payload) {
    // A new tab carries no navigation state, so a handoff cannot ride along.
    // Opening the target empty-handed would be worse than not opening it: the
    // plugin believes it handed something over and the user lands on a blank
    // arche wondering what happened. Same tab, always.
    const routed = opts.openHandoff?.(opts.payload, targetArcheId, {
      originArcheId: opts.originArcheId,
    });
    // No provider (or a target that refuses): fall back to a plain navigate
    // rather than dropping the user nowhere. The intake ignores a prefill it
    // never received, which is honest degradation — silence is not.
    if (routed) return;
    navigate(url);
    return;
  }

  if (opts.newTab) {
    const opened = windowOpen(url, '_blank', 'noopener,noreferrer');
    if (!opened) navigate(url);
    return;
  }
  navigate(url);
}

interface PluginRendererProps {
  archeId: string;
  /**
   * `arches.is_trusted` — threaded from ArcheInteractionPage's metadata fetch
   * (the `getArche` response this component fetches does not carry it). Gates
   * the RUNTIME Fias AI declaration lane: non-trusted plugins are
   * manifest-only, because manifest action text is fixed at publish and passes
   * submission review, while runtime text is never reviewed. Defaults false
   * (fail closed).
   */
  isTrusted?: boolean;
}

/** Matches PluginConfig from @fias/db-types (redefined for client use) */
interface PluginConfig {
  bundleRef: string;
  manifestVersion: string;
  // Full client-safe permission union from the SDK (lockstep with db-types) —
  // see the note in PluginBridge.ts. Avoids the prior hand-listed subset that
  // omitted most scopes.
  permissions: PluginPermission[];
  sdkVersion: string;
  bundleSizeBytes: number;
  buildHash: string;
  themeId?: string;
  fontPairingId?: string;
  customTheme?: import('@fias/arche-sdk').FiasTheme;
  /** Manifest `fiasAI` block snapshotted at publish (null = explicitly none). */
  fiasAI?: { description: string; actions: PluginDeclaredAiAction[] } | null;
}

/**
 * Hard cap on the MERGED (manifest ∪ runtime) action list — the assistant
 * turn endpoint's `.strict()` schema rejects >50 actions, which would 400
 * every turn while the plugin is open. Manifest actions win the trim
 * (deterministic, and they passed publish review).
 */
const MAX_MERGED_AI_ACTIONS = 50;

/** After the user closes or minimizes the assistant panel, plugin `ai_open`
 * requests are ignored for this long — the user's dismissal outranks the
 * plugin's desire to reopen (bridge-level burst debounce is separate). */
const AI_OPEN_AFTER_DISMISS_COOLDOWN_MS = 30_000;

/** availableWhen: dumb equality against the plugin's last state snapshot. */
function aiActionAvailable(
  action: PluginDeclaredAiAction,
  state: Record<string, unknown>,
): boolean {
  if (!action.availableWhen) return true;
  return Object.entries(action.availableWhen).every(([key, value]) => state[key] === value);
}

/**
 * Strip to EXACTLY the fields the assistant turn schema accepts —
 * `availableWhen` (or any future host-only field) leaking through would 400
 * the whole turn (`archeActionSchema` is `.strict()`).
 */
function toArcheAIAction(a: PluginDeclaredAiAction): ArcheAIAction {
  return {
    id: a.id,
    description: a.description,
    parameterSchema: a.parameterSchema,
    ...(a.requiresConfirmation !== undefined
      ? { requiresConfirmation: a.requiresConfirmation }
      : {}),
    ...(a.category !== undefined ? { category: a.category } : {}),
  };
}

/**
 * Mints a purchase attempt id: opaque, URL-safe, within the server's
 * `attemptId` bounds (8–128 chars of [A-Za-z0-9_-]). `crypto.randomUUID` is
 * unavailable in some non-secure/embedded contexts, hence the fallback.
 */
function mintPurchaseAttemptId(): string {
  const raw =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random()
          .toString(36)
          .slice(2)}`;
  return `att_${raw.replace(/-/g, '')}`;
}

/** sessionStorage key for a pending attempt — survives a reload mid-purchase. */
function purchaseAttemptStorageKey(archeId: string, productIdentifier: string): string {
  return `fias:purchase-attempt:${archeId}:${productIdentifier}`;
}

/**
 * How long a pending attempt id stays reusable, mirroring
 * `IAP_PURCHASE_REPLAY_WINDOW_MS` in `@fias/db-types` (this package cannot
 * depend on it — see the note in `client/src/lib/api.ts`). The server REFUSES
 * a replay older than its own window with `IDEMPOTENCY_CONFLICT`, so sending
 * an expired id can only produce an error; discarding it here turns a
 * two-click recovery into one. Keep the two values in step.
 */
const PURCHASE_ATTEMPT_TTL_MS = 15 * 60 * 1000;

interface PendingPurchaseAttempt {
  id: string;
  mintedAt: number;
}

function parsePendingAttempt(raw: string): PendingPurchaseAttempt | null {
  try {
    const parsed = JSON.parse(raw) as Partial<PendingPurchaseAttempt>;
    if (typeof parsed?.id === 'string' && typeof parsed?.mintedAt === 'number') {
      return { id: parsed.id, mintedAt: parsed.mintedAt };
    }
  } catch {
    // Not JSON — a bare id written by an older build. Treat it as expired
    // rather than guessing an age: the server would refuse it anyway once its
    // window had passed, and a fresh id always works.
  }
  return null;
}

/**
 * Returns the attempt id to send for this (arche, product): the pending one
 * from an earlier confirmation that got no definitive answer and is still
 * inside the replay window, else a fresh one. Persisted best-effort to
 * sessionStorage; every storage access is wrapped because it can throw in
 * private/embedded contexts.
 */
function takePurchaseAttemptId(
  pending: Map<string, PendingPurchaseAttempt>,
  archeId: string,
  productIdentifier: string,
): string {
  const key = purchaseAttemptStorageKey(archeId, productIdentifier);
  const fresh = (a: PendingPurchaseAttempt | null | undefined): boolean =>
    !!a && Date.now() - a.mintedAt < PURCHASE_ATTEMPT_TTL_MS;

  const inMemory = pending.get(key);
  if (fresh(inMemory)) return inMemory!.id;
  try {
    const raw = sessionStorage.getItem(key);
    const stored = raw ? parsePendingAttempt(raw) : null;
    if (fresh(stored)) {
      pending.set(key, stored!);
      return stored!.id;
    }
  } catch {
    // storage unavailable — in-memory only
  }
  const minted: PendingPurchaseAttempt = { id: mintPurchaseAttemptId(), mintedAt: Date.now() };
  pending.set(key, minted);
  try {
    sessionStorage.setItem(key, JSON.stringify(minted));
  } catch {
    // storage unavailable — in-memory only
  }
  return minted.id;
}

/** The server answered definitively: the attempt is spent, forget its id. */
function settlePurchaseAttempt(
  pending: Map<string, PendingPurchaseAttempt>,
  archeId: string,
  productIdentifier: string,
): void {
  const key = purchaseAttemptStorageKey(archeId, productIdentifier);
  pending.delete(key);
  try {
    sessionStorage.removeItem(key);
  } catch {
    // storage unavailable — nothing to clear
  }
}

/** Origin of the plugin-content CDN for this deployment, or '' when none. */
function pluginContentOrigin(): string {
  const base = import.meta.env.VITE_PLUGIN_CONTENT_CDN_BASE as string | undefined;
  if (!base) return '';
  try {
    return new URL(base).origin;
  } catch {
    return '';
  }
}

/**
 * A plugin, mounted once per acting workspace: "Reopen in <workspace>" (see
 * PluginWorkspaceIndicator) bumps the key, so the plugin starts fresh — new
 * bridge, new subscriptions, nothing it read in the old workspace carried
 * over — pinned to the workspace the user now stands in.
 */
export function PluginRenderer(props: PluginRendererProps) {
  const [mount, setMount] = useState(0);
  return <PluginRendererMount key={mount} {...props} onReopen={() => setMount((n) => n + 1)} />;
}

function PluginRendererMount({
  archeId,
  isTrusted = false,
  onReopen,
}: PluginRendererProps & { onReopen?: () => void }) {
  const [pluginConfig, setPluginConfig] = useState<PluginConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  const [srcdoc, setSrcdoc] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  // The plugin's SDK has answered `init` with `ready`. Distinct from
  // `iframeReady` (the DOM load event): between the two, a postMessage lands in
  // a frame with no listener. Anything pushed one-way waits for THIS.
  const [pluginReady, setPluginReady] = useState(false);
  // No latest-ref mirror needed: readiness now lives on the bridge
  // (`handoffReadiness()`), which `deliverHandoff` reads through `bridgeRef` at
  // call time — so there is no stale state to close over.
  // When the plugin's SDK announces a protocol version below what the
  // host requires, we capture the version pair and render an "update
  // needed" affordance instead of leaving a misbehaving iframe mounted.
  const [incompatibleSdk, setIncompatibleSdk] = useState<{
    sdkVersion: number;
    minRequired: number;
  } | null>(null);
  // A retired op-family alias (storage_*/data_*/workspace_*/store_*) arrived
  // from an old, immutable bundle (ADR-012 Phase L Job 3 Step 2). Same "needs
  // update" treatment as an incompatible SDK, but triggered on first use of the
  // retired capability rather than at the init handshake.
  const [retiredCapability, setRetiredCapability] = useState<string | null>(null);

  // Purchase confirmation state
  const [purchaseProduct, setPurchaseProduct] = useState<{
    productIdentifier: string;
    name: string;
    description: string | null;
    priceCredits: number;
    type: string;
  } | null>(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [userBalance, setUserBalance] = useState<number | null>(null);
  const purchaseResolveRef = useRef<((value: unknown) => void) | null>(null);
  /**
   * Purchase idempotency keys, one per (arche, product), sent as `attemptId`.
   * The server keys the charge on it, so the SAME id on a retry replays the
   * original purchase instead of charging twice.
   *
   * The id is retained ONLY while the outcome is genuinely unknown — a
   * transport failure, where the charge may have committed but the response
   * never arrived. It survives a page reload (sessionStorage), so the user's
   * next confirmation of the same product reuses it and the server replays
   * rather than re-charging.
   *
   * Any answer FROM the server spends it, including an HTTP rejection
   * (402 INSUFFICIENT_CREDITS, 409 IDEMPOTENCY_CONFLICT, 403
   * ACCOUNT_REQUIRED): the server has spoken, so there is nothing left to
   * replay, and re-sending a spent id would be an unbreakable 409 loop once
   * the server's replay window closed.
   *
   * It also expires locally after `PURCHASE_ATTEMPT_TTL_MS`, mirroring the
   * server's window — an id the server will certainly refuse is not worth
   * sending. See `takePurchaseAttemptId` / `settlePurchaseAttempt`.
   */
  const purchaseAttemptIdsRef = useRef<Map<string, PendingPurchaseAttempt>>(new Map());

  // Arche identity for the host consent sheets, captured from the mount fetch
  // (see loadPlugin) so a sheet never opens missing the facts it exists to show.
  const [archeDisplayName, setArcheDisplayName] = useState<string | null>(null);

  // Vault user-document picker state (vault_documents_pick host mediation)
  const [vaultPickRequest, setVaultPickRequest] = useState<{
    maxDocuments: number;
    access: 'read' | 'write';
    documentId?: string;
    accept?: string[];
    /** This request's consent-sheet ticket — see `consentSheetRef`. */
    seq: number;
  } | null>(null);

  // THE host consent sheet. The pick and save sheets both put a
  // decision in front of the user on a plugin's say-so, so they share ONE slot:
  //
  //   - at most one is open. Were each only exclusive with itself, a plugin
  //     could stack an "allow editing?" pick over the save sheet the user is
  //     about to confirm and turn that click into an edit grant. A newcomer is
  //     declined with its documented outcome (a canceled pick, SAVE_DECLINED).
  //   - every request holds a TICKET (`seq`), and a sheet can settle only the
  //     request it was opened for. A sheet does async work (the grant POST)
  //     and can outlive its request — an arche switch settles the
  //     request and unmounts the sheet, but the POST still completes and calls
  //     `onResolve`. Resolving "whatever is pending now" would hand arche A's
  //     picked document ids and names to arche B's promise. The ticket also
  //     keys the pick sheet, so nothing a previous request left behind (its
  //     selected files, its step) is painted under the next one's consent copy.
  const consentSheetRef = useRef<{
    seq: number;
    settle: (result: unknown) => void;
    cancel: () => void;
  } | null>(null);
  const consentSheetSeqRef = useRef(0);

  // A handoff that arrived before the iframe finished its bridge handshake.
  // The `open_document` navigation path lands here on nearly every cold open
  // (we mount, then the iframe loads), so without the queue the common case —
  // "open this attachment in the editor" from a cold start — would silently
  // do nothing. Latest-wins: a second handoff supersedes an undelivered first.
  const pendingHandoffRef = useRef<ArcheHandoffPayload | null>(null);
  /** Arche this component last reset for — `null` before the first run. */
  const lastResetArcheRef = useRef<string | null>(null);
  // Monotonic delivery ticket. Only the newest handoff may dispatch, so an
  // out-of-order grant completion cannot open a superseded document.
  const handoffSeqRef = useRef(0);

  // A plugin saving into the user's Documents (`upload({ destination:
  // 'documents' })` / `saveCopy`): the host Save dialog, which performs the
  // write from the user's own session once they confirm. The bytes the plugin
  // handed over wait here; the plugin never learns the folder.
  const [vaultSaveRequest, setVaultSaveRequest] = useState<{
    request: VaultSaveRequestFromBridge;
    seq: number;
  } | null>(null);

  /** Open a consent sheet, or decline with `declined` when one is already up
   *  (a vault sheet, or the purchase sheet — the same stacking argument). */
  const openConsentSheet = useCallback(
    <T,>(declined: T, show: (seq: number) => void, hide: () => void): Promise<T> => {
      if (consentSheetRef.current || purchaseResolveRef.current) return Promise.resolve(declined);
      return new Promise<T>((resolve) => {
        consentSheetSeqRef.current += 1;
        const seq = consentSheetSeqRef.current;
        consentSheetRef.current = {
          seq,
          settle: (result) => {
            resolve(result as T);
            hide();
          },
          cancel: () => {
            resolve(declined);
            hide();
          },
        };
        show(seq);
      });
    },
    [],
  );

  /** Settle the request holding ticket `seq`. A stale ticket — a sheet whose
   *  request was already settled (arche switch, unmount) — is ignored. */
  const settleConsentSheet = useCallback((seq: number, result: unknown) => {
    const open = consentSheetRef.current;
    if (!open || open.seq !== seq) return;
    consentSheetRef.current = null;
    open.settle(result);
  }, []);

  /** Settle whatever is open as declined — whoever awaits it is owed an answer. */
  const cancelOpenConsentSheet = useCallback(() => {
    const open = consentSheetRef.current;
    consentSheetRef.current = null;
    open?.cancel();
  }, []);

  // Fias AI plane: the plugin's runtime action declaration (trusted arches
  // only) + its last state snapshot. Both are React state — the registered
  // capabilities object is rebuilt per render and read fresh at turn time, so
  // a ref-only snapshot would hand the assistant a stale availableWhen filter.
  const [runtimeAiDecl, setRuntimeAiDecl] = useState<PluginRuntimeAiDeclaration | null>(null);
  const [aiState, setAiState] = useState<Record<string, unknown>>({});
  // Latest-ref mirror for the synchronous getStateSnapshot contract.
  const aiStateRef = useRef(aiState);
  aiStateRef.current = aiState;
  // The bridge is constructed once per iframe load; read trust via ref so the
  // callback can't capture a stale value (same idiom as themeModeRef).
  const isTrustedRef = useRef(isTrusted);
  isTrustedRef.current = isTrusted;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<PluginBridgeHost | null>(null);
  const navigate = useNavigate();
  // The `/a/:id` segment the user actually arrived under. For plugin arches
  // that is normally the SLUG — `archePath()` links to the slug and
  // ConditionalProtectedRoute deliberately does NOT redirect `arc_*` arches to
  // their canonical id — so it differs from the `archeId` prop. The bridge
  // needs it to resolve the plugin's own `navigateTo()` calls onto the URL the
  // page is already on: rewriting the segment mid-session changes the `:id`
  // param, which tears this renderer down and restarts the plugin.
  //
  // Latest-ref for the same reason as themeModeRef below: the bridge is built
  // once, at iframe load, and would otherwise close over the first value.
  const { id: urlSegment } = useParams<{ id: string }>();
  const urlSegmentRef = useRef(urlSegment);
  urlSegmentRef.current = urlSegment;
  const location = useLocation();
  // Fias Forge test channel (ADR-023): `/a/<archeId>?channel=test` renders the
  // forge's current TEST build for its owner instead of the live
  // `plugin_config`. The bundle, the bridge token and the bridge tenant all
  // switch together (see loadPlugin + handleIframeLoad); the sandbox flags,
  // CSP and bridge transport are exactly the production ones.
  const testChannel = new URLSearchParams(location.search ?? '').get('channel') === 'test';
  const { user } = useAuth();
  const { theme: appTheme } = useTheme();
  const themeMode = appTheme === 'light' ? 'light' : 'dark';
  // The bridge's `getTheme` callback must read the CURRENT mode through this
  // ref, never the `themeMode` binding above. The bridge is constructed once,
  // at iframe load, so a plain closure captures the mode of THAT render; every
  // later `sendThemeUpdate()` then re-evaluates `getTheme` inside the stale
  // closure and pushes the ORIGINAL theme — the plugin re-renders with the
  // theme it already had, and the toggle appears dead until a hard reload
  // rebuilds the bridge. (`handleIframeLoad` lists `themeMode` in its deps,
  // but a fresh callback identity is useless: the iframe never fires `load`
  // again, so the fresh closure is never invoked.)
  //
  // Written during render (the latest-ref idiom) rather than in an effect, on
  // purpose: an effect version would tie correctness to effect ORDERING — the
  // sendThemeUpdate effect below must observe the new value in the same
  // commit — which is a subtler dependency than an idempotent render write.
  // Same idiom as the builder page's previewThemeRef and panel-kit's queueRef.
  const themeModeRef = useRef(themeMode);
  themeModeRef.current = themeMode;
  // Same latest-ref treatment for the user: `getUser` used to close over
  // `user` with the identical trap (listed in the callback deps, but the
  // iframe never re-fires `load`, so a fresh closure never installs). User
  // identity is stable within a session so it was latent rather than
  // user-visible — fixed for uniformity so the bridge options carry ONE idiom,
  // not a mix of live refs and stale closures.
  const userRef = useRef(user);
  userRef.current = user;
  // The text-model catalog for plugins that can invoke a model — the same
  // cached `/v1/models` query the platform's own model pickers use. Fetched
  // only for `entities:invoke` plugins; read through a ref for the same reason
  // as the theme above, and pushed with `sendTextModelsUpdate` once loaded.
  const pluginTextModels = usePluginTextModelCatalog(
    pluginConfig?.permissions.includes('entities:invoke') ?? false,
  );
  const pluginTextModelsRef = useRef(pluginTextModels);
  pluginTextModelsRef.current = pluginTextModels;

  // The workspace the plugin acts in: offered to the plugin bridge as the
  // acting Holder for Vault document reads (consented and, under a workspace
  // approval, the whole workspace) and to the consent sheets as the Holder to
  // share FROM. `useHolderSwitcher` masks guild spaces (a plugin surface never
  // opts in), so this is Personal or an org workspace — and only an ORG one is
  // named: Personal is the server's default.
  //
  // PINNED FOR THIS MOUNT (ADR-022). The switcher is global and shared across
  // tabs; following it live let a plugin left open in one tab read whichever
  // workspace the user moved to in another. The first resolved value is kept;
  // a later switch shows a banner offering to reopen the plugin there
  // (`onReopen` remounts it), and the plugin never follows on its own.
  const { selectedOption: actingHolderOption, isLoading: holderSwitcherLoading } =
    useHolderSwitcher();
  const liveOrgHolder =
    actingHolderOption?.holderType === 'org'
      ? { holderId: actingHolderOption.holderId, displayName: actingHolderOption.displayName }
      : null;
  const [pinnedOrgHolder, setPinnedOrgHolder] = useState<
    { holderId: string; displayName: string } | null | undefined
  >(undefined);
  useEffect(() => {
    if (pinnedOrgHolder === undefined && !holderSwitcherLoading) setPinnedOrgHolder(liveOrgHolder);
  }, [pinnedOrgHolder, holderSwitcherLoading, liveOrgHolder]);
  const actingOrgHolder = pinnedOrgHolder ?? null;
  const workspaceSwitchedTo =
    pinnedOrgHolder !== undefined &&
    !holderSwitcherLoading &&
    (liveOrgHolder?.holderId ?? null) !== (pinnedOrgHolder?.holderId ?? null)
      ? { displayName: actingHolderOption?.displayName ?? 'your Personal vault' }
      : null;
  // Only a plugin that reads the user's documents acts through the workspace.
  const readsUserDocuments =
    pluginConfig?.permissions.includes('vault:user-documents:read' as PluginPermission) ?? false;
  // Via a ref for the bridge, which reads it at call time (the `userRef` idiom).
  const actingOrgHolderRef = useRef(actingOrgHolder);
  actingOrgHolderRef.current = actingOrgHolder;

  // Fetch arche data to get plugin config
  useEffect(() => {
    let mounted = true;

    // Reset per-arche state FIRST. The effect is keyed on `archeId` but the
    // component is reused across arche-to-arche navigation, so without this the
    // previous plugin's `srcdoc` and `pluginConfig` stay mounted while the next
    // one loads — rendering arche A's bundle under arche B's permission grant.
    setLoading(true);
    setError(null);
    setSrcdoc(null);
    setPluginConfig(null);
    setIframeReady(false);
    setPluginReady(false);
    setIncompatibleSdk(null);
    setRetiredCapability(null);
    // Per-arche host state must reset with the arche, for the same reason the
    // bundle does. A handoff queued for arche A would otherwise be flushed into
    // arche B's iframe when B reports ready — handing the document reference
    // the user aimed at A to a different plugin entirely. An open save sheet
    // would keep showing A's file name and A's folder over B, and confirming it
    // would resolve a promise belonging to a bridge that is already destroyed.
    // Retire the OUTGOING arche's bridge here, not when the next iframe
    // happens to load. Left alive it is still `bridgeRef.current`, so a late or
    // repeated `ready` from arche A could flip `pluginReady` for arche B and a
    // handoff queued for B would be dispatched through A's bridge — into the
    // wrong plugin. Destroying it also settles anything A had pending.
    bridgeRef.current?.destroy();
    bridgeRef.current = null;
    // Clear the queued handoff ONLY when the arche actually changed.
    //
    // This effect is keyed on `archeId`, but "keyed on" is not "runs only when
    // it changes": React re-runs a setup after any cleanup, and under
    // StrictMode that is every mount, twice. Effects run in declaration order
    // and the handoff intake is declared BELOW this one, so an unconditional
    // clear here wipes a payload the intake queued moments earlier in the
    // previous pass — and the intake will not re-queue it, because
    // consume-once (correctly) refuses to apply the same history entry twice.
    // The result was a return handoff that arrived, queued, and then silently
    // vanished before the plugin ever reported ready.
    //
    // The cross-arche danger the clear exists for is unaffected: leaving A for
    // B still drops A's payload rather than flushing it into B's iframe.
    if (lastResetArcheRef.current !== null && lastResetArcheRef.current !== archeId) {
      pendingHandoffRef.current = null;
    }
    lastResetArcheRef.current = archeId;
    // Supersede any grant still in flight for the outgoing arche — its
    // completion must not dispatch into the incoming one.
    handoffSeqRef.current += 1;
    setArcheDisplayName(null);
    // Settle, don't drop: whoever is awaiting it is owed an answer. This also
    // retires its ticket, so the outgoing sheet's in-flight work can no longer
    // settle anything (see `consentSheetRef`).
    cancelOpenConsentSheet();

    async function loadPlugin() {
      try {
        const result = (await api.getArche(
          archeId,
          testChannel ? { channel: 'test' } : undefined,
        )) as Record<string, unknown>;
        // `mounted` guards these for the same reason it guards the bundle
        // below, and it matters MORE here: on an A→B switch where A's request
        // finishes last, an unguarded write would label B's consent sheet with
        // A's name and A's folder while the server writes to B's. A consent
        // screen naming the wrong app and the wrong destination is worse than
        // one that names neither.
        //
        // Captured here rather than re-fetched per request: this endpoint
        // already returns both, and fetching them when the sheet opens paints
        // an empty destination line that fills in a beat later — long enough
        // for a quick confirm to approve without ever seeing where the file goes.
        if (!mounted) return;
        setArcheDisplayName((result.name as string | undefined) ?? null);
        let config: PluginConfig | null;
        if (testChannel) {
          // A forge's arche is usually a DRAFT with no `plugin_config` at all,
          // so the test channel never reads it: the build's permission
          // snapshot + hash come from the owner-only test-channel endpoint
          // (404 `NO_TEST_BUILD` until the forge has produced a ready build).
          const testBuild = await api.getForgeTestChannel(archeId);
          config = {
            bundleRef: '',
            manifestVersion: '',
            permissions: testBuild.permissions as PluginPermission[],
            sdkVersion: '',
            bundleSizeBytes: 0,
            buildHash: testBuild.buildHash ?? '',
            fiasAI: null,
          };
        } else {
          config = result.pluginConfig as PluginConfig | null;
        }

        if (!config) {
          throw new Error('This arche does not have a valid plugin configuration');
        }

        // EVERY bundle is fetched through the API proxy — there is deliberately
        // no direct-load branch. The proxy resolves the bundle server-side from
        // `archeId` alone (`bundleRef` is never sent), so a malformed or
        // hostile `bundleRef` cannot steer the browser at a third-party origin.
        // The previous `https://`-and-not-S3 escape hatch was unreachable (the
        // publish paths only ever write `plugin-bundles/…` or `s3://…`) and
        // failed OPEN, which is the wrong direction for a sandbox boundary.
        //
        // credentials (sessionCredentials()) are required so the fias_session cookie flows
        // to the API on production (frontend and API live on different
        // subdomains; default fetch drops cookies on cross-origin). Without
        // it, signed-in users hit 401 on non-public plugins because
        // optionalAuth sees no token. Same pattern as api.ts:181.
        // Cache-bust on the build hash (defence in depth over the server's
        // ETag): the bundle URL is otherwise version-stable, so a stale
        // HTTP-cache entry from a prior publish could be reused even if an
        // intermediary ignores the response's Cache-Control. The hash is
        // fetched in this same effect run, so it always matches the config
        // we're about to render. NOTE: this only affects the NEXT mount /
        // navigation / reload — the effect is keyed on [archeId] and never
        // re-runs for an already-mounted plugin when a new version publishes.
        // The test channel asks for the forge's test build (`channel=test`)
        // and never touches the live bundle path; the server serves it
        // `no-store`, the hash is still appended so two builds never share a
        // cache entry.
        const bundleQuery = new URLSearchParams();
        if (testChannel) bundleQuery.set('channel', 'test');
        if (config.buildHash) bundleQuery.set('h', config.buildHash);
        const bundleQueryString = bundleQuery.toString();
        const bundleUrl = bundleQueryString
          ? `${API_BASE}/arches/${archeId}/plugin-bundle?${bundleQueryString}`
          : `${API_BASE}/arches/${archeId}/plugin-bundle`;
        const resp = await fetch(bundleUrl, {
          credentials: sessionCredentials(),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => null);
          const msg =
            (body as Record<string, Record<string, string>> | null)?.error?.message ||
            `Failed to load plugin bundle (${resp.status})`;
          throw new Error(msg);
        }
        const html = await resp.text();
        // The bundle was built with `__FIAS_API_BASE__` placeholders in
        // `<base href>` and the CSP `img-src` / `media-src` / `font-src`
        // allowlists. The placeholder is mis-named historically — it's
        // only used for plugin-asset URLs (the bridge call goes via
        // postMessage; the iframe CSP is `connect-src 'none'`).
        //
        // Prefer the dedicated plugin-assets CDN when configured —
        // CloudFront caches the immutable, content-addressed `/v1/arches/
        // <id>/plugin-assets/<hash>/<file>` paths so repeated viewers don't
        // hit the API tier for each asset. Falls back to the API origin
        // when the env var is empty (local dev + envs before the CDN
        // stack deploys). Either origin serves the same path
        // (CloudFront forwards uncached requests to the same route),
        // so changing the substitution origin is a strict caching
        // optimization with no semantic difference.
        const pluginAssetsCdnBase = import.meta.env.VITE_PLUGIN_ASSETS_CDN_BASE as
          | string
          | undefined;
        const pluginAssetsOrigin =
          pluginAssetsCdnBase && pluginAssetsCdnBase.length > 0
            ? pluginAssetsCdnBase.replace(/\/$/, '')
            : API_ORIGIN;
        // Content-pack streaming origin (ADR-021): the build writes
        // `__FIAS_CONTENT_BASE__` into img-src / media-src / font-src for
        // plugins that may call client-side entities. Where no content CDN is
        // configured the placeholder is removed, which only tightens the policy.
        const substituted = html
          .split('__FIAS_API_BASE__')
          .join(pluginAssetsOrigin)
          .split('__FIAS_CONTENT_BASE__')
          .join(pluginContentOrigin());
        if (mounted) {
          setSrcdoc(substituted);
          setPluginConfig(config);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load plugin');
          setLoading(false);
        }
      }
    }

    loadPlugin();
    return () => {
      mounted = false;
    };
  }, [archeId, testChannel, cancelOpenConsentSheet]);

  // Set up bridge when iframe loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current || !pluginConfig) return;

    setIframeReady(true);

    // Clean up previous bridge
    bridgeRef.current?.destroy();

    bridgeRef.current = new PluginBridgeHost({
      // Test channel: the bridge acts as the synthetic `test_<archeId>` tenant
      // (DataStore / storage / workspaces isolated from production), and the
      // token minted below carries that same id as its `arche` claim.
      archId: testChannel ? testTenantIdForArche(archeId) : archeId,
      testChannel,
      permissions: pluginConfig.permissions,
      iframe: iframeRef.current,
      onResize: (height) => setIframeHeight(height),
      onToast: (message, variant) => {
        toast({
          description: message,
          variant: variant === 'error' ? 'destructive' : undefined,
        });
      },
      onNavigate: (path, { replace }) => {
        navigate(path, { replace });
      },
      getUrlSegment: () => urlSegmentRef.current,
      onOpenArche: (targetArcheId, opts) =>
        openArcheFromHost(navigate, targetArcheId, {
          ...opts,
          openHandoff: fiasAIGlobalRef.current?.openHandoff,
        }),
      // Runtime Fias AI declaration — TRUSTED arches only (manifest text is
      // publish-reviewed; runtime text is not). The bridge has already
      // enforced permission + bounds; trust is the renderer's to decide
      // because it holds the metadata.
      onAiActionsDeclare: (declaration) => {
        if (!isTrustedRef.current) {
          addBreadcrumb('plugin-ai', 'runtime ai_actions_declare ignored (arche not trusted)', {
            archeId,
          });
          return;
        }
        setRuntimeAiDecl(declaration);
      },
      onAiState: (state) => setAiState(state),
      onAiOpenRequest: () => {
        const ctx = fiasAIGlobalRef.current;
        if (!ctx) return;
        if (ctx.isPanelOpen && !ctx.isMinimized) return; // already visible
        // A MINIMIZED panel is a standing dismissal, not a gap to jump
        // through: the user parked it (possibly before this renderer even
        // mounted, where the falling-edge tracker below can't have seen the
        // transition). Expanding it is the plugin overriding the user.
        if (ctx.isMinimized) {
          addBreadcrumb('plugin-ai', 'ai_open ignored (assistant is minimized by the user)', {
            archeId,
          });
          return;
        }
        if (Date.now() - lastPanelDismissAtRef.current < AI_OPEN_AFTER_DISMISS_COOLDOWN_MS) {
          addBreadcrumb('plugin-ai', 'ai_open ignored (user recently dismissed the panel)', {
            archeId,
          });
          return;
        }
        ctx.openPanel();
      },
      getTheme: () => {
        if (pluginConfig.customTheme) return pluginConfig.customTheme;
        if (pluginConfig.themeId) {
          const entry = getThemeById(pluginConfig.themeId);
          if (entry) return entry.theme;
        }
        // Via the ref, NOT the closure — see themeModeRef above.
        return themeModeRef.current === 'dark' ? DARK_THEME : LIGHT_THEME;
      },
      // Self-hosted vendored-library asset URLs (only consulted for
      // `sandbox:vendored-libraries` plugins). Resolved from the client's
      // platform-resources CDN env; empty when unconfigured (local dev / pre-CDN
      // staging) → plugin degrades. The full registry is injected; the CSP
      // (widened per declared dep at build time) is what gates actual loading.
      getVendoredAssets: () =>
        allVendoredLibraryAssets(
          normalizeCdnBaseUrl(
            import.meta.env.VITE_PLATFORM_RESOURCES_CDN_DOMAIN as string | undefined,
          ),
        ),
      // Via the ref, NOT the closure — see pluginTextModelsRef above.
      getTextModels: () => pluginTextModelsRef.current,
      getUser: async () => ({
        // Via the ref, NOT the closure — see userRef above.
        userId: userRef.current?.userId ?? '',
        displayName: userRef.current?.username ?? 'User',
        avatar: null,
      }),
      // Via the ref, NOT the closure — see actingOrgHolderRef above.
      getActingHolderId: () => actingOrgHolderRef.current?.holderId ?? null,
      bridgeApiUrl: API_BASE,
      // Plugin iframes have an opaque origin and cannot send the
      // parent's session cookie. They authenticate to the bridge with
      // a per-arche capability JWT (audience=plugin-bridge, 5min TTL,
      // archeId-scoped). The token has NO authority outside the
      // /v1/plugins/bridge/:archId route — a leak cannot drain credits
      // or read vault data via any other endpoint.
      getAuthToken: async () =>
        (await authApi.getPluginBridgeToken(archeId, testChannel ? 'test' : undefined)) ?? '',
      // One-shot expired-token recovery (see callBackendOp): bust the cached
      // token so the retry mints fresh instead of re-sending the stale one.
      onAuthTokenExpired: () =>
        authApi.invalidatePluginBridgeToken(archeId, testChannel ? 'test' : undefined),
      onIncompatibleSdk: (sdkVersion, minRequired) => {
        setIncompatibleSdk({ sdkVersion, minRequired });
      },
      onRetiredCapability: (op) => {
        setRetiredCapability(op);
      },
      onPurchaseRequest: (product) => {
        // Never over a consent sheet: a purchase confirmation appearing under
        // the cursor of a user about to answer a different question is the
        // same click-swap the consent sheets guard against among themselves.
        if (consentSheetRef.current) {
          return Promise.resolve({ success: false, error: 'USER_CANCELED' });
        }
        return new Promise((resolve) => {
          // Fetch user balance for the confirmation sheet
          api
            .get<{ balance: number }>('/credits/balance')
            .then((res) => setUserBalance(res.data?.balance ?? null))
            .catch(() => setUserBalance(null));
          setPurchaseProduct(product);
          purchaseResolveRef.current = resolve;
        });
      },
      onVaultPickRequest: (request) =>
        // Declined while another consent sheet is up — and here that is a
        // consent boundary, not tidiness: a request carries the ACCESS being
        // asked for, and a newcomer replacing the sheet on screen would turn
        // the "allow reading" the user is about to confirm into "allow
        // editing", with the button under their cursor. A cancel is a
        // documented outcome, and the request the user is looking at still
        // settles.
        openConsentSheet<VaultPickResolution>(
          { canceled: true },
          (seq) => setVaultPickRequest({ ...request, seq }),
          () => setVaultPickRequest(null),
        ),
      onPluginReady: () => setPluginReady(true),
      onVaultSaveRequest: (request) =>
        // A declined newcomer is `SAVE_DECLINED` to the plugin — a documented
        // outcome the caller already handles — rather than a promise stranded
        // behind a dialog that never opens.
        openConsentSheet<VaultSaveResolution>(
          { canceled: true },
          (seq) => setVaultSaveRequest({ request, seq }),
          () => setVaultSaveRequest(null),
        ),
    });

    bridgeRef.current.init();
    // NOTE: `themeMode` and `user` are deliberately NOT deps — `getTheme` and
    // `getUser` read the live values through refs. Listing them only churns
    // the callback identity, which cannot help (the iframe's `load` event has
    // already fired) and previously disguised the stale-closure bug as fixed.
  }, [archeId, testChannel, pluginConfig, navigate, openConsentSheet]);

  // Send theme update when mode changes
  useEffect(() => {
    bridgeRef.current?.sendThemeUpdate();
  }, [themeMode]);

  // Deliver the text-model catalog when it loads (usually after init).
  useEffect(() => {
    if (pluginTextModels) bridgeRef.current?.sendTextModelsUpdate();
  }, [pluginTextModels]);

  // Push host-side URL changes into the plugin. The plugin stays mounted
  // across its own sub-paths, so without this its `currentPath` is frozen at
  // whatever `init` reported and the browser back button silently desyncs the
  // two: the address bar moves, the plugin does not.
  //
  // Fired unconditionally on every pathname change — including the plugin's
  // own `navigateTo` arriving back here through `onNavigate`. The bridge drops
  // the redundant ones, so there is no "did this originate in the plugin?"
  // bookkeeping to get wrong. Before the iframe loads there is no bridge yet,
  // and none is needed: `init` reads the path live when it finally goes out.
  useEffect(() => {
    bridgeRef.current?.sendNavigationUpdate();
  }, [location.pathname]);

  // Fias AI plane — declarations are per-plugin; drop them when the arche
  // swaps (the bridge is torn down with the old iframe alongside).
  useEffect(() => {
    setRuntimeAiDecl(null);
    setAiState({});
  }, [archeId]);

  const manifestAi = pluginConfig?.fiasAI ?? null;

  // `ai_open` (discoverability Part 2): the bridge debounces bursts; the
  // renderer owns panel-state policy because only IT can see the panel. Two
  // rules on top of the bridge's: no-op while the panel is visible, and a
  // cooldown after the user closes/minimizes it — "get out of my way" must
  // stick against a plugin that immediately re-requests.
  const fiasAIGlobal = useGlobalFiasAIOptional();
  const fiasAIGlobalRef = useRef(fiasAIGlobal);
  fiasAIGlobalRef.current = fiasAIGlobal;
  const lastPanelDismissAtRef = useRef(0);
  const panelWasVisibleRef = useRef(false);
  useEffect(() => {
    const visible = !!fiasAIGlobal && fiasAIGlobal.isPanelOpen && !fiasAIGlobal.isMinimized;
    if (panelWasVisibleRef.current && !visible) lastPanelDismissAtRef.current = Date.now();
    panelWasVisibleRef.current = visible;
  }, [fiasAIGlobal]);

  // Manifest ∪ runtime, manifest winning id collisions (it passed publish
  // review; a runtime redefinition of a reviewed action must not stick).
  const mergedAiActions = useMemo(() => {
    const runtime = isTrusted ? runtimeAiDecl : null;
    const merged: PluginDeclaredAiAction[] = [...(manifestAi?.actions ?? [])];
    const ids = new Set(merged.map((a) => a.id));
    for (const action of runtime?.actions ?? []) {
      if (ids.has(action.id)) continue;
      ids.add(action.id);
      merged.push(action);
    }
    if (merged.length > MAX_MERGED_AI_ACTIONS) {
      // Never silent: the trim is visible in telemetry, and deterministic
      // (manifest first) so which verbs survive doesn't depend on timing.
      addBreadcrumb('plugin-ai', 'merged AI action list trimmed to the turn-schema cap', {
        archeId,
        declared: merged.length,
        cap: MAX_MERGED_AI_ACTIONS,
      });
      return merged.slice(0, MAX_MERGED_AI_ACTIONS);
    }
    return merged;
  }, [manifestAi, runtimeAiDecl, isTrusted, archeId]);

  const visibleAiActions = useMemo(
    () => mergedAiActions.filter((a) => aiActionAvailable(a, aiState)).map(toArcheAIAction),
    [mergedAiActions, aiState],
  );

  /** Whether this arche is a registered "open a document in…" destination. */
  // `hasOwnProperty`, not `in`: `in` walks the prototype chain, so `/a/toString`
  // or `/a/constructor` would register as a document-intake target and get an
  // assistant registration for an arche that does not exist.
  //
  // Only `open_document` counts here: this flag drives the ASSISTANT-facing
  // registration (`acceptPrefill`, the in-place path). Reply kinds are
  // deliberately excluded — they arrive on the navigation path below, which is
  // unconditional, and a plugin receiving one has nothing to advertise to the
  // assistant.
  const isDocumentIntakeTarget =
    Object.prototype.hasOwnProperty.call(ARCHE_HANDOFF_INTAKE, archeId) &&
    ARCHE_HANDOFF_INTAKE[archeId].kinds.includes('open_document');

  // ── Arche Handoff relay (host → plugin) ────────────────────────────────
  //
  // The platform half of plugin document intake. Internal arches implement
  // intake themselves (`useArcheHandoffIntake` + `acceptPrefill`); a plugin
  // cannot, because its code runs in the iframe — so the host consumes both
  // paths on its behalf and forwards the payload over the bridge.
  //
  // Being registered in ARCHE_HANDOFF_INTAKE is what makes an arche a
  // destination; both intake paths already gate on it before reaching here.
  const deliverHandoff = useCallback(
    (payload: ArcheHandoffPayload): boolean => {
      const bridge = bridgeRef.current;
      if (!bridge) return false;

      const readiness = bridge.handoffReadiness();
      // Not up yet — the caller queues and we retry on `onPluginReady`.
      if (readiness === 'not-ready') return false;
      // Built against an SDK predating the handoff lane: it will ignore the
      // message and the lane cannot report that. Say so once and stop, rather
      // than mint a grant and post into silence — "nothing happened" is the
      // exact outcome the capability flag exists to prevent. Reported as
      // HANDLED (true) because the caller's fallback is to navigate here,
      // which lands in the same place and would toast twice.
      if (readiness === 'unsupported') {
        addBreadcrumb('arche-handoff', 'Plugin SDK predates the handoff lane', { archeId });
        toast({
          title: 'This app can’t open files yet',
          description: 'It needs an update from its developer before it can receive files.',
          variant: 'destructive',
        });
        return true;
      }

      // A handoff carries a document REFERENCE, and a plugin cannot read one
      // it was not granted — the consented-read verbs resolve against grant
      // rows, and only the host picker mints those. Without this the whole
      // flow is inert: the user says "open this here", the plugin receives an
      // id, and every read comes back DOCUMENT_NOT_FOUND.
      //
      // So the host mints the grant, as it does for the picker, through the
      // same host-session route (`POST /vault/consents/arche` — a plugin can
      // never mint its own). Choosing this file for this app IS the consent;
      // the grant is per-document, audited, and revocable in Vault → App
      // Access like any other. There is deliberately no second confirmation
      // screen: the user just performed the action, and a modal restating it
      // would only teach people to click through the ones that matter.
      //
      // Returning true means "taken", not "delivered" — `acceptPrefill` must
      // answer synchronously, so the grant and the dispatch finish after it.
      // The grant is a round-trip, so delivery lands after this function
      // returns — which opens two windows the pre-ready queue's latest-wins
      // rule does not cover:
      //
      //   * two handoffs in quick succession can have their grants resolve out
      //     of order, delivering the EARLIER document last and opening a file
      //     the user has already moved on from;
      //   * the arche can change mid-grant, leaving `bridge` destroyed and its
      //     iframe gone, with the dispatch still aimed at it.
      //
      // A monotonic ticket closes both: only the newest request may deliver,
      // and the arche-ID reset bumps it so everything in flight is superseded.
      // The bridge-identity check states the second invariant explicitly rather
      // than relying on the ticket to imply it.
      const ticket = ++handoffSeqRef.current;
      void (async () => {
        try {
          if (payload.kind === 'open_document') {
            // READ ONLY, always. This grant is made with no sheet — the user
            // chose "open with", not "let this app change my file" — so it
            // must never carry `access`. Edit access is only ever granted from
            // a consent screen that says so (VaultAccessGrantSheet). Pinned by
            // tests/architecture/vault-edit-grants-only-from-consent-screens.test.ts.
            //
            // The handoff names a document, not its Holder: the server grants
            // on whichever workspace the document lives in.
            await api.post('/vault/consents/arche', {
              granteeArcheId: archeId,
              documentIds: [payload.documentId],
            });
          }
          if (ticket !== handoffSeqRef.current) return;
          if (bridgeRef.current !== bridge) return;
          bridge.dispatchHandoff(payload);
        } catch (err) {
          // Tell the user, because nothing else will: the lane is one-way, so
          // a silent failure looks exactly like "the app ignored my file".
          if (ticket !== handoffSeqRef.current) return;
          addBreadcrumb('arche-handoff', 'Failed to grant handoff document', { archeId });
          toast({
            title: 'Couldn’t open that file here',
            description: isApiErrorCode(err, 'ARCHE_CANNOT_USE_GRANT')
              ? 'This app isn’t set up to open your files.'
              : 'Granting access to the file failed. Try opening it from the app instead.',
            variant: 'destructive',
          });
        }
      })();
      return true;
    },
    [archeId],
  );

  // Navigation path: we're already on the plugin's page (or just landed on
  // it). Queue when the iframe isn't up yet and flush on ready.
  useArcheHandoffIntake(archeId, (payload) => {
    const delivered = deliverHandoff(payload);
    if (!delivered) pendingHandoffRef.current = payload;
    // Same reasoning as the breadcrumb in the intake hook: the relay is
    // one-way, so a payload that reaches here and then goes nowhere is
    // indistinguishable from one that never arrived unless we say which
    // branch it took.
    addBreadcrumb('arche-handoff', delivered ? 'Relayed to plugin' : 'Queued until plugin ready', {
      archeId,
      kind: payload.kind,
    });
  });

  useEffect(() => {
    if (!pluginReady) return;
    const queued = pendingHandoffRef.current;
    if (!queued) return;
    pendingHandoffRef.current = null;
    addBreadcrumb('arche-handoff', 'Flushed queued handoff on plugin ready', {
      archeId,
      kind: queued.kind,
    });
    deliverHandoff(queued);
  }, [pluginReady, deliverHandoff, archeId]);

  // Register with the global Fias AI window — the same UI plane every
  // internal arche page uses; the assistant sees a plugin arche exactly the
  // way it sees email or image-editor. The description ALWAYS carries the
  // full capability summary (including availableWhen-hidden verbs) so a
  // filtered-out action reads as "needs a document open first", never
  // "can't do that".
  //
  // Registration is NOT gated on having actions alone: an arche registered for
  // document intake must also register, because `acceptPrefill` — the
  // apply-in-place half of a handoff — is delivered through this same entry.
  // The image editor is the worked example: zero declared actions, but it is
  // an "open this file in…" destination.
  useRegisterArcheAI(
    mergedAiActions.length > 0 || isDocumentIntakeTarget
      ? {
          archeId,
          enabled: true,
          archeDescription: [manifestAi?.description, isTrusted ? runtimeAiDecl?.description : null]
            .filter(Boolean)
            .join('\n'),
          actions: visibleAiActions,
          // TRUST BOUNDARY (ADR-017): the snapshot returned here is sent as
          // archeContext.state and interpolated VERBATIM into the assistant's
          // system prompt server-side. For a non-trusted arche that would be
          // an unreviewed free-text channel into the prompt — the exact thing
          // the manifest-only policy exists to prevent — so untrusted state
          // stays HOST-SIDE ONLY: it still drives availableWhen filtering
          // above, but the assistant sees an empty snapshot.
          getStateSnapshot: () => (isTrusted ? aiStateRef.current : {}),
          // In-place path: the assistant hands a document to an arche that is
          // ALREADY open. Returning false (iframe not up yet) is not a
          // failure — the caller falls back to navigation-with-state, which
          // the intake hook above then picks up.
          acceptPrefill: deliverHandoff,
          applyAction: (actionId, params) => {
            // Only declared ids are dispatchable — the model cannot probe the
            // iframe with arbitrary action names.
            if (!mergedAiActions.some((a) => a.id === actionId)) {
              throw new Error(`Unknown action "${actionId}" — the plugin does not declare it.`);
            }
            const bridge = bridgeRef.current;
            if (!bridge) throw new Error('The plugin is not running.');
            // Async relay: resolves on the plugin's ai_action_result; the
            // assistant loop awaits this before telling the model it applied.
            return bridge.dispatchAiAction(actionId, params);
          },
          sessionKey: `plugin:${archeId}`,
        }
      : null,
  );

  // Clean up bridge and pending purchase on unmount
  useEffect(() => {
    return () => {
      if (purchaseResolveRef.current) {
        purchaseResolveRef.current({ success: false, error: 'USER_CANCELED' });
        purchaseResolveRef.current = null;
      }
      consentSheetRef.current?.cancel();
      consentSheetRef.current = null;
      bridgeRef.current?.destroy();
    };
  }, []);

  // What the Save dialog may offer the arche afterwards. Only what it can hold
  // at all — the server re-decides both from the published manifest.
  const permissions = pluginConfig?.permissions ?? [];
  const mayKeepReading = permissions.includes('vault:user-documents:read');
  const mayKeepEditing = mayKeepReading && permissions.includes('vault:user-documents:edit');

  const handlePurchaseConfirm = useCallback(async () => {
    if (!purchaseProduct || !bridgeRef.current) return;
    setPurchaseLoading(true);
    try {
      // Same-origin call — use cookie auth (the api wrapper handles
      // credentials:'include' + CSRF). No bearer token round-trip
      // needed; this isn't running inside the sandboxed iframe.
      //
      // The store_purchase alias was retired (ADR-012 Phase L Job 3 Step 2); the
      // host executes the confirmed charge through the generic entity_invoke op
      // against the store entity. The server wraps the store result in `{ output }`
      // (mirrors handleStoreOp's entity_invoke store branch in PluginBridge.ts),
      // so unwrap it back to the legacy bare shape the sheet's resolver expects.
      // Reuse the attempt id from an earlier confirmation of this product that
      // never got a definitive answer; mint one otherwise.
      const attemptId = takePurchaseAttemptId(
        purchaseAttemptIdsRef.current,
        archeId,
        purchaseProduct.productIdentifier,
      );
      const { data } = await api.post<{ output?: unknown }>(`/plugins/bridge/${archeId}`, {
        type: 'entity_invoke',
        payload: {
          entityId: PLUGIN_STORE_ENTITY_ID,
          payload: {
            op: 'purchase',
            productIdentifier: purchaseProduct.productIdentifier,
            attemptId,
          },
        },
      });
      // A 2xx reply is DEFINITIVE — the server has spoken. The id is spent.
      settlePurchaseAttempt(
        purchaseAttemptIdsRef.current,
        archeId,
        purchaseProduct.productIdentifier,
      );
      const result = ((data as { output?: unknown })?.output ?? data) as {
        success: boolean;
        error?: string;
      };
      purchaseResolveRef.current?.(result);
    } catch (err) {
      // `ApiError` is only ever constructed from a real HTTP response
      // (client/src/lib/api.ts), so it is the exact test for "the server
      // answered". Anything else — DNS, timeout, connection reset, a lost
      // response — is a transport failure.
      if (err instanceof ApiError) {
        // DEFINITIVE rejection (402 INSUFFICIENT_CREDITS, 409
        // IDEMPOTENCY_CONFLICT, 403 ACCOUNT_REQUIRED, …). The attempt is
        // spent: keeping the id would re-send it forever, and once the
        // server's replay window has closed that is an unbreakable 409 loop
        // on a product the user can no longer buy. Clearing it means the
        // next confirmation mints a fresh id and starts a real purchase.
        settlePurchaseAttempt(
          purchaseAttemptIdsRef.current,
          archeId,
          purchaseProduct.productIdentifier,
        );
        purchaseResolveRef.current?.({
          success: false,
          error: err.code ?? 'Purchase failed',
        });
      } else {
        // Transport failure: the charge MAY have committed. The attempt id is
        // deliberately KEPT so the next confirmation of this product replays
        // on the server instead of charging again.
        purchaseResolveRef.current?.({ success: false, error: 'Purchase failed' });
      }
    } finally {
      setPurchaseLoading(false);
      setPurchaseProduct(null);
      purchaseResolveRef.current = null;
    }
  }, [purchaseProduct, archeId]);

  const handlePurchaseCancel = useCallback(() => {
    purchaseResolveRef.current?.({ success: false, error: 'USER_CANCELED' });
    setPurchaseProduct(null);
    purchaseResolveRef.current = null;
  }, []);

  if (loading) {
    return <ArcheLoadingScreen />;
  }

  if (error) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Plugin Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pluginConfig) return null;

  if (incompatibleSdk) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Plugin needs update
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              This plugin was built against an older version of the Fias SDK (v
              {incompatibleSdk.sdkVersion}) than the platform currently supports (v
              {incompatibleSdk.minRequired}+). The plugin author needs to rebuild with an updated
              SDK.
            </p>
            <p className="text-sm text-muted-foreground">
              If you're the contributor, run{' '}
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                pnpm up @fias/arche-sdk
              </code>{' '}
              in your plugin project and publish a new version.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (retiredCapability) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Plugin needs update
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              This plugin uses a capability that has been retired from the Fias platform. The plugin
              author needs to rebuild it against a current version of the Fias SDK.
            </p>
            <p className="text-sm text-muted-foreground">
              If you're the contributor, run{' '}
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                pnpm up @fias/arche-sdk
              </code>{' '}
              in your plugin project and publish a new version.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Always the proxied document — `bundleRef` is never used as an iframe `src`.
  // `?? ''` is a fail-safe: an empty iframe, never a third-party origin.
  const iframeProps = { srcDoc: srcdoc ?? '' };

  return (
    <div
      className={
        testChannel
          ? 'relative w-full h-full min-h-0 flex flex-col'
          : 'relative w-full h-full min-h-0'
      }
    >
      {testChannel && (
        <div
          role="status"
          data-testid="forge-test-channel-banner"
          className="flex-shrink-0 border-b border-amber-300 bg-amber-50 px-3 py-1.5 text-center text-xs font-medium text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200"
        >
          Test build — only you can see this
        </div>
      )}
      <iframe
        ref={iframeRef}
        {...iframeProps}
        onLoad={handleIframeLoad}
        sandbox={PLUGIN_IFRAME_SANDBOX}
        allow={PLUGIN_IFRAME_ALLOW}
        referrerPolicy="no-referrer"
        className="w-full border-0 block"
        style={{
          height: iframeHeight != null ? `${iframeHeight}px` : '100%',
          background: 'hsl(var(--background))',
          visibility: iframeReady ? 'visible' : 'hidden',
        }}
        title={`Plugin: ${archeId}`}
      />
      {!iframeReady && (
        <div className="absolute inset-0">
          <ArcheLoadingScreen />
        </div>
      )}
      <PurchaseConfirmationSheet
        open={purchaseProduct !== null}
        product={purchaseProduct}
        userBalance={userBalance}
        loading={purchaseLoading}
        onConfirm={handlePurchaseConfirm}
        onCancel={handlePurchaseCancel}
      />
      {/* Each sheet's `onResolve` carries ITS request's ticket — see
          `consentSheetRef`. Keyed by it too, so a sheet never outlives its
          request into the next one. */}
      {vaultPickRequest !== null && (
        <VaultAccessGrantSheet
          key={vaultPickRequest.seq}
          open
          archeId={archeId}
          archeName={archeDisplayName}
          maxDocuments={vaultPickRequest.maxDocuments}
          access={vaultPickRequest.access}
          documentId={vaultPickRequest.documentId}
          // The picker browses the acting workspace when it is an org one (the
          // sheet resolves Personal itself when this is null); the grant lands
          // on whichever workspace the picked documents live in.
          holderId={actingOrgHolder?.holderId ?? null}
          holderLabel={actingOrgHolder?.displayName ?? null}
          accept={fileAcceptFromTypes(vaultPickRequest.accept)}
          onResolve={(result) => settleConsentSheet(vaultPickRequest.seq, result)}
        />
      )}
      {vaultSaveRequest !== null && (
        <SaveFileDialog
          key={vaultSaveRequest.seq}
          open
          saver={{
            kind: 'plugin',
            archeId,
            archeName: archeDisplayName,
            mayKeepReading,
            mayKeepEditing,
          }}
          suggestedName={vaultSaveRequest.request.fileName}
          mimeType={vaultSaveRequest.request.mimeType}
          initialHolderId={actingOrgHolder?.holderId ?? null}
          getBytes={async () =>
            new Blob([vaultSaveRequest.request.bytes], { type: vaultSaveRequest.request.mimeType })
          }
          onSaved={({ document, access }) =>
            settleConsentSheet(vaultSaveRequest.seq, {
              saved: { documentId: document.documentId, name: document.name, access },
            } satisfies VaultSaveResolution)
          }
          onOpenChange={(open) => {
            if (!open) settleConsentSheet(vaultSaveRequest.seq, { canceled: true });
          }}
        />
      )}
      {/* Host-chrome discoverability for the plugin's Fias AI surface —
          rendered only once the iframe is up (a chip over the loading
          screen reads as UI noise) and only when the plugin actually
          declares actions. See FiasAIDiscoveryChip for the rules. */}
      {iframeReady && mergedAiActions.length > 0 && <FiasAIDiscoveryChip archeId={archeId} />}
      {iframeReady && readsUserDocuments && (
        <PluginWorkspaceIndicator
          pinned={actingOrgHolder}
          switchedTo={workspaceSwitchedTo}
          onReopen={() => onReopen?.()}
        />
      )}
    </div>
  );
}
